/* eslint-disable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-explicit-any */
import { createFileRoute, Link, Outlet, useMatches, useParams, useNavigate } from '@tanstack/react-router'
import { ArrowLeft } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import PageTransition from '@/components/PageTransition'
import { ListSkeleton } from '@/components/molecules/ListSkeleton'
import { useServiceCaseDetail } from '@/hooks/useServiceCases'
import { cn } from '@/lib/utils'
import { useTranslation } from 'react-i18next'

const ROLLUP_FIELDS = [
  { key: 'commercial_status', label: 'Commercial', color: 'bg-blue-100 text-blue-800' },
  { key: 'appointment_status', label: 'Appointment', color: 'bg-green-100 text-green-800' },
  { key: 'fulfillment_status', label: 'Fulfillment', color: 'bg-purple-100 text-purple-800' },
  { key: 'finance_status', label: 'Finance', color: 'bg-amber-100 text-amber-800' },
  { key: 'risk_status', label: 'Risk', color: 'bg-red-100 text-red-800' },
] as const

const CASE_TABS = [
  { to: 'overview', segment: 'overview', label: 'caseDetail:overview' },
  { to: 'estimates', segment: 'estimates', label: 'caseDetail:estimates' },
  { to: 'appointments', segment: 'appointments', label: 'caseDetail:appointments' },
  { to: 'parts', segment: 'parts', label: 'caseDetail:parts' },
  { to: 'audit', segment: 'audit', label: 'caseDetail:audit' },
] as const

export const Route = createFileRoute('/_authenticated/service-cases/$id')({
  component: CaseDetailLayout,
})

function CaseDetailLayout() {
  const { t } = useTranslation()
  const { id } = useParams({ from: '/_authenticated/service-cases/$id' })
  const navigate = useNavigate()
  const matches = useMatches()
  const activeTab = matches[matches.length - 1]?.routeId?.split('/').pop() ?? 'overview'

  const { data: caseData, isLoading } = useServiceCaseDetail(id)
  const sc = (caseData as any)?.data ?? caseData

  if (isLoading) return <PageTransition><ListSkeleton /></PageTransition>
  if (!sc) return <PageTransition><p>{t('caseDetail:serviceCaseNotFound')}</p></PageTransition>

  return (
    <PageTransition>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate({ to: '/service-cases' })}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="flex-1">
            <h1 className="text-2xl font-semibold">{sc.case_number}</h1>
            <div className="flex items-center gap-2 mt-1">
              <Badge variant="outline">{sc.state?.replace(/_/g, ' ')}</Badge>
              {sc.priority && <Badge variant="secondary">{sc.priority}</Badge>}
            </div>
          </div>
        </div>

        {/* Rollup Status Cards */}
        <div className="grid grid-cols-5 gap-2">
          {ROLLUP_FIELDS.map(({ key, label, color }) => (
            <Card key={key} className="text-center">
              <CardContent className="p-3">
                <div className="text-xs text-muted-foreground mb-1">{label}</div>
                <Badge className={sc[key] && sc[key] !== 'none' ? color : 'bg-muted text-muted-foreground'}>
                  {sc[key] || 'none'}
                </Badge>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Tab bar */}
        <div className="flex gap-1 border-b">
          {CASE_TABS.map(tab => (
            <Link
              key={tab.to}
              to={`/service-cases/${id}/${tab.to}`}
              className={cn(
                'flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors',
                activeTab === tab.segment
                  ? 'border-primary text-foreground'
                  : 'border-transparent text-muted-foreground hover:text-foreground',
              )}
            >
              {t(tab.label, tab.segment)}
            </Link>
          ))}
        </div>

        <Outlet />
      </div>
    </PageTransition>
  )
}
